// https://hello.atlassian.net/wiki/spaces/~474271760/pages/2427503547/Solution+Approach+Use+of+aggregation+from+CCP+search+on+customer+addresses
// In this component, following things are done:
// 1. For aggregation of ship to parties, we are using CCP search.
// 2. On expanding row, we are calling CCP search again.
// 3. On successful editing/adding customer address, we are collapsing all expanded rows.
import React, { useCallback, useState } from 'react';
import styled from '@emotion/styled';
import { token } from '@atlaskit/tokens';
import { Link } from 'react-router-dom';
import { ApolloError, ApolloQueryResult } from '@apollo/client';
import {
  ColGroup,
  CustomSearch,
  ExpandableRow,
  OpenSidebarButton,
  TableBody,
  TableHead,
  useFeatureToggle,
} from '@css-xp/base';
import isEmail from 'validator/lib/isEmail';
import { CustomDynamicResultTable } from '@css-xp/base/custom-dynamic-result-table';
import { trackSearchEvent } from '@atlassian/temp-analytics';
import Tooltip, { TooltipPrimitive } from '@atlaskit/tooltip';
import { abgGraphqlClient, ProxyGraphClients } from '@atlassian/temp-client-utils';
import { CommerceAddress, Maybe } from '@css-xp/graphtypes/src/generated-types-new';
import { COMMERCIAL_PRICE_ELIGIBILITY, ENABLE_DESIGN_ITERATION_1, routePaths } from '@css-xp/ppc-constants';
import { getCompanyNameRoute, getRouteUrl } from '@css-xp/ppc-utils';
import { DEFAULT_ROWS_PER_PAGE } from '@atlassian/cssxp-utils-entitlement';
import { emptyStateProp, errorStateProp, tableErrorMessage } from '@css-xp/ppc-common';
import {
  AggregatedShipToParty,
  AggregatedShipToPartyConnection,
  AggregatedShipToPartyOptions,
  FetchShipToPartiesForTransactionAccountPpcQuery,
  FetchShipToPartiesForTransactionAccountPpcQueryVariables,
  PartnerSearchOptions,
} from '@css-xp/ppc-graphtypes';
import { ADVOCATE_ACCESS_TYPE, ADVOCATE_RESOURCE_TYPE, AdvocateAccessWall } from '@css-xp/ppc-access-control';
import { customEmptyStateProp } from '@css-xp/ppc-common/src/state-props';
import LockIcon from '@css-xp/base/assets/img/icon-lock.svg';
import { fetchShipToPartiesForTransactionAccountPPC } from '../../../queries';
import './sold-to-address-table.scss';
import {
  ColumnList,
  extractPriceEligibility,
  formatAddressLabel,
  FormData,
  getHeadersForTable,
  getTaxExemptStatus,
  SIDEBAR,
  sidebarType,
} from './search-sold-to-address-table';

export interface SoldToAddressTableProps {
  addressResponse?: any;
  tableLoading: boolean;
  searchTerm?: string;
  transactionAccountId: string;
  tableRefetch: (
    variables:
      | Partial<{
          aggregatedShipToPartyOptions: AggregatedShipToPartyOptions;
        }>
      | undefined
  ) => Promise<ApolloQueryResult<unknown>>;
  billToAddress?: CommerceAddress | null;
  hidePricingEligibility?: boolean;
  showAddAddress?: boolean;
  currentPage?: number;
  rowsPerPage?: number;
  tableLevelLoading: boolean;
  showLoadMore?: any;
  onLoadMore?: any;
  totalCount?: number;
  onRowChange?: (arg0: number) => void;
  onPageChange?: (arg0: number) => void;
  hideActions?: boolean;
  showSearchBox?: boolean;
  hideEdit?: boolean;
  onSearch?: (value: string) => void;
  hideAddFromBillToAddress?: boolean;
  showAddAddressInRow?: boolean;
  error?: string | ApolloError;
  searchOptions?: PartnerSearchOptions;
  emptyState?: any;
  heading?: React.ReactNode;
}

const InlineDialog = styled(TooltipPrimitive)`
  background: white;
  border-radius: ${token('border.radius', '4px')};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  box-sizing: content-box;
  padding: ${token('space.100', '8px')} ${token('space.150', '12px')};
  max-width: 330px;
`;

const EditSoldToAddressDisabledMessage = () => (
  <div>
    This sold-to address cannot be edited due to the price eligibility applied to it. For more information please reach
    out to Partner Service Desk.
  </div>
);

export const SearchSoldToAddressTableV2 = ({
  addressResponse,
  tableLoading,
  searchTerm,
  transactionAccountId,
  billToAddress,
  rowsPerPage,
  totalCount = 0,
  currentPage,
  hidePricingEligibility = false,
  showAddAddress = false,
  hideActions = false,
  showSearchBox = true,
  onRowChange,
  showLoadMore,
  onLoadMore,
  onPageChange,
  onSearch,
  tableRefetch,
  hideEdit = false,
  hideAddFromBillToAddress,
  showAddAddressInRow = false,
  error,
  tableLevelLoading,
  searchOptions,
  emptyState = emptyStateProp,
  heading,
}: SoldToAddressTableProps) => {
  const [isEnabled] = useFeatureToggle();
  const [subRows, setSubRows] = useState<Map<string, CommerceAddress[]>>(new Map());
  const [companyNameToSubRowsLoading, setCompanyNameToSubRowsLoading] = useState<Map<string, boolean>>(new Map());
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isSearched, setIsSearched] = useState(false);
  const isEmailTerm = searchTerm && isEmail(searchTerm);
  const shipToParties = addressResponse;
  const ppcBffGraphqlClient = React.useMemo(() => abgGraphqlClient(ProxyGraphClients.PPC_BFF), []);
  const buildShipToPartyOptions = (companyName: string) => {
    return {
      pageOptions: {
        cursor: null,
        size: 100,
      },
      searchOptions: searchOptions,
      filterOptions: {
        names: [companyName],
      },
    };
  };

  const renderActionsOnTop = useCallback(() => {
    return [
      () =>
        showAddAddress ? (
          <div className="add-sold-to-address add">
            <div className="add-action">
              <AdvocateAccessWall accessType={ADVOCATE_ACCESS_TYPE.CREATE} resource={ADVOCATE_RESOURCE_TYPE.CUSTOMER}>
                <OpenSidebarButton
                  sidebar={SIDEBAR.ADD_SOLD_TO_ADDRESS}
                  sidebarOptions={{
                    sidebarWidth: '600px',
                    isOverlay: true,
                  }}
                  payload={{
                    transactionAccountId,
                    billToAddress,
                    refetch: tableRefetch,
                    onSubmitComplete: handleOnSidebarSubmitCompleted,
                    hidePricingEligibility,
                    sidebarType,
                    hideAddFromBillToAddress,
                    contentString: {
                      HEADING: 'Add customer',
                      SUBMIT_BUTTON: 'Add customer',
                      NOTIFICATION_SUCCESS_HEADING: 'Customer added',
                    },
                  }}
                  appearance="default"
                  testId="add-sold-to-address"
                  className="add-customer-btn"
                >
                  Add new customer
                </OpenSidebarButton>
              </AdvocateAccessWall>
            </div>
          </div>
        ) : (
          <div />
        ),
    ];
  }, [transactionAccountId, billToAddress]);

  const handleOnSearch = useCallback(
    async (formData: FormData) => {
      const searchInput = formData.searchTerm;
      setIsSearched(!!searchInput);
      if (onSearch) {
        onSearch(searchInput);
      }
      trackSearchEvent();
    },
    [tableRefetch, onSearch]
  );

  const renderFilters = (showSearchBox: boolean) => {
    if (!showSearchBox) {
      return <div />;
    }
    return (
      <div className="sold-to-table-filter">
        <CustomSearch
          placeholder="Search"
          onSearch={handleOnSearch}
          isLoading={tableLoading}
          isClearable={true}
          helperMessage="Enter company name or sold-to address"
        />
      </div>
    );
  };

  const getColumns = useCallback(
    (hideActions: boolean, hideEdit: boolean) => {
      return getHeadersForTable(hideActions, hideEdit);
    },
    [hideActions, hideEdit, showAddAddressInRow]
  );

  const handleOnSidebarSubmitCompleted = () => {
    setIsExpanded(false);
  };

  const getColumnData = (visibleColumns: ColumnList[], item: any, isSecondaryRow = false) => {
    const customerAddressPath = getRouteUrl(routePaths.customerAddressDetailsById, {
      txaId: transactionAccountId,
      addressId: item.id,
    });
    const companyNamePath = getCompanyNameRoute({ txaId: transactionAccountId, companyName: item.name });
    return visibleColumns.map((column) => {
      switch (column.id) {
        case 'companyName':
          return (
            <td>
              <Link
                to={companyNamePath}
                data-testid="company-name-link"
                className="company-name"
                onClick={(e) => e.stopPropagation()}
              >
                {item.name}
              </Link>
            </td>
          );
        case 'address':
          return (
            <td>
              <Link to={customerAddressPath} data-testid="customer-address-link" onClick={(e) => e.stopPropagation()}>
                {formatAddressLabel(item)}
              </Link>
            </td>
          );
        case 'pricingEligibility':
          return <td>{extractPriceEligibility(item)}</td>;
        case 'taxExempt':
          return <td>{getTaxExemptStatus(item)}</td>;
        case 'edit':
          return (
            <>
              {isSecondaryRow ? <td /> : null}
              <td>
                {!hideEdit &&
                  (item?.priceEligibility?.toLowerCase() === COMMERCIAL_PRICE_ELIGIBILITY.toLowerCase() ? (
                    <AdvocateAccessWall
                      accessType={ADVOCATE_ACCESS_TYPE.UPDATE}
                      resource={ADVOCATE_RESOURCE_TYPE.SHIP_TO_ADDRESS}
                    >
                      <OpenSidebarButton
                        sidebar={SIDEBAR.EDIT_SOLD_TO_ADDRESS}
                        payload={{
                          shipToAddress: item,
                          transactionAccountId,
                          billToAddress,
                          pricingEligibility: item?.shipToPriceEligibility,
                          refetch: tableRefetch,
                          hidePricingEligibility,
                          sidebarType,
                          onSubmitComplete: handleOnSidebarSubmitCompleted,
                        }}
                        appearance="link"
                        sidebarOptions={{
                          isOverlay: true,
                          sidebarWidth: '600px',
                        }}
                        testId="edit-ship-to-party-action"
                        spacing="none"
                      >
                        Edit
                      </OpenSidebarButton>
                    </AdvocateAccessWall>
                  ) : (
                    <Tooltip
                      testId="sold-to-address-edit-disabled-tooltip"
                      component={InlineDialog}
                      content={<EditSoldToAddressDisabledMessage />}
                    >
                      {/* @ts-ignore */}
                      <img src={LockIcon} data-testid="lock-icon" />
                    </Tooltip>
                  ))}
              </td>
            </>
          );
      }
    });
  };

  const hasNoResults = totalCount === 0;

  const getSubRowsForCompanyName = useCallback(
    (companyName: string, index: number) => {
      const subRowsForCompanyName = subRows.get(companyName) || [];
      // sorting for subRows based on addressLine1 to maintain the same order on UI as well as exported CSV
      subRowsForCompanyName.sort((a, b) => (a?.addressLine1 ?? '').localeCompare(b?.addressLine1 ?? '') ?? 0);
      return subRowsForCompanyName.map((shipToParty) => {
        return (
          <tr
            data-testid={`ship-to-table-row-${shipToParty?.id}`}
            key={`ship-to-table-row-${index}-${shipToParty?.id}`}
            className="grouped-child"
          >
            {getColumnData(getColumns(hideActions, hideEdit), shipToParty, true)}
          </tr>
        );
      });
    },
    [subRows]
  );

  const getSubRowsLoadingForCompanyName = useCallback(
    (companyName: string) => {
      return companyNameToSubRowsLoading.get(companyName) ?? true;
    },
    [companyNameToSubRowsLoading]
  );

  const handleOnExpandRow = async (shipToPartyGroup: AggregatedShipToParty) => {
    setIsExpanded(true);
    const companyName = shipToPartyGroup.key;
    setCompanyNameToSubRowsLoading((map) => new Map(map.set(companyName, true)));
    const shipToPartiesResponse = await ppcBffGraphqlClient.query<
      FetchShipToPartiesForTransactionAccountPpcQuery,
      FetchShipToPartiesForTransactionAccountPpcQueryVariables
    >({
      query: fetchShipToPartiesForTransactionAccountPPC,
      variables: {
        id: transactionAccountId,
        shipToPartyOptions: buildShipToPartyOptions(companyName),
      },
      fetchPolicy: 'network-only',
    });
    const subLoading = shipToPartiesResponse.loading;
    setCompanyNameToSubRowsLoading((map) => new Map(map.set(companyName, subLoading)));
    if (!subLoading) {
      setSubRows(
        (map) => new Map(map.set(companyName, shipToPartiesResponse.data?.transactionAccount.shipToParty?.nodes ?? []))
      );
    }
  };

  const renderRowWithGroupedData = (index: number, shipToPartyGroup: AggregatedShipToParty) => {
    if (shipToPartyGroup?.data?.length === 1) {
      return (
        <tr
          data-testid={`ship-to-table-row-${index}`}
          key={`ship-to-table-row-${index}-${shipToPartyGroup?.key}`}
          className="ungrouped-child"
        >
          {getColumnData(getColumns(hideActions, hideEdit), shipToPartyGroup.data[0])}
        </tr>
      );
    }
    const companyNamePath = getCompanyNameRoute({ txaId: transactionAccountId, companyName: shipToPartyGroup.key });

    const rows = [
      [
        <td key="1">
          <Link to={companyNamePath} className="company-name" onClick={(e) => e.stopPropagation()}>
            {shipToPartyGroup.key}
          </Link>
        </td>,
        <td key="2" />,
        <td key="3" />,
        <td key="4" />,
      ],
    ];

    return (
      <ExpandableRow
        key={index}
        rowsAsCells={rows}
        subRows={getSubRowsForCompanyName(shipToPartyGroup.key, index)}
        isSubRowsLoading={getSubRowsLoadingForCompanyName(shipToPartyGroup.key)}
        isDefaultExpanded={isExpanded}
        onExpand={async () => {
          await handleOnExpandRow(shipToPartyGroup);
        }}
      />
    );
  };

  const renderTableBody = (shipToParties: Maybe<AggregatedShipToParty>[] | null | undefined) => {
    return (shipToParties ?? [])?.map((item, index) => renderRowWithGroupedData(index, item as AggregatedShipToParty));
  };
  const emptyStateProps = isEnabled(ENABLE_DESIGN_ITERATION_1)
    ? customEmptyStateProp(renderActionsOnTop())
    : emptyState;
  return (
    <div className="sold-to-address-container">
      <CustomDynamicResultTable
        innerHeading={heading}
        testId="shipping-details"
        isLoading={tableLoading && tableLevelLoading}
        totalCount={totalCount}
        noResults={shipToParties?.length === 0}
        isPaginationEnabled={isEmailTerm ? false : totalCount > DEFAULT_ROWS_PER_PAGE}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        showLoadMore={showLoadMore}
        loadMoreLoading={!tableLevelLoading && tableLoading}
        onLoadMore={onLoadMore}
        onRowChange={onRowChange}
        selectableResults={true}
        error={error}
        actions={renderActionsOnTop()}
        boxShadow={true}
        className="sold-to-address-table"
        filters={renderFilters(showSearchBox)}
        shouldForceEnableFilters={true}
        emptyStateProps={emptyStateProps}
        errorStateProps={errorStateProp}
        isSearched={isSearched}
      >
        <ColGroup columns={getColumns(hideActions, hideEdit)} />
        <TableHead columns={getColumns(hideActions, hideEdit)} />
        <TableBody>{renderTableBody(shipToParties)}</TableBody>
      </CustomDynamicResultTable>
    </div>
  );
};
